import { Component, OnInit, OnDestroy, TemplateRef, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Store } from '@ngxs/store';
import { Alert, AlertService, AlertType, ModalComponent, TableComponent } from '@michelin/theme';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { CasingSearchService } from '../../services/casing-search.services';
import { CasingSearchRequest } from '../../model/casing-search-request.model';
import { PaginatedCasingSearchResponse } from '../../model/casing-search-response.model';
import {
  CasingSearchState,
  UpdateCasingSearchResultsAction,
  ClearCasingSearchResultsAction,
  UpdateSearchCriteriaAction,
  UpdateIsRestoreStateAction
} from '../../../../shared/state/CasingSearchState';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { AutocompleteOption } from '../../model/casing-auto-complete.model';
import { DealerState, UpdatePlantsAction, UpdateSitesAction } from '../../../../shared/state/DealerState';
import { PlantService } from '../../../../shared/services/plant.service';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { SiteService } from '../../../../shared/services/site.service';
import { ProcessCodeService } from 'src/app/shared/services/process-code.service';
import { CasingDropdownDataState } from 'src/app/shared/state/CasingDropdownState';
import { Site } from '../../../../shared/services/models/Site';
import { Plant } from '../../../plant-setup/model/Plant';
import { HttpErrorResponse } from '@angular/common/http';
import { CreateWorkOrderService } from '../../../workorder/components/createworkorder/create-worker-order.service';
import { RestDealerService } from '../../../../shared/dealer/services/rest-dealer.service';
import { CasingDropdownDataService } from '../../services/casing-dropdown-data.service';
import { RouteTrackerService } from '../../../workorder/services/route-tracker.service';
import { optionalBrandNumber, optionalNumber, sanitizeBrandNumber } from '../../../workorder/shared/util/work-order-validators';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';

@Component({
  selector: 'app-search-casings',
  templateUrl: './search-casings.component.html',
  styleUrls: ['./search-casings.component.scss'],
  standalone: false
})
export class SearchCasingsComponent implements OnInit, OnDestroy {
  searchCasingsForm: FormGroup;
  searchResults$: Observable<PaginatedCasingSearchResponse | null> = of(null);
  isLoading = false;
  errorMessage = '';
  casingMakeList: string[];
  casingSeriesList: string[];
  casingSizeList: string[];
  casingTreadList: string[];
  casingTreadWidthList: string[];
  casingGradeList: string[];
  userLogin: string;
  plantId: string;
  casingLocationList: any[] = [];
  siteNames: Site[] = [];
  plantNames: Plant[] = [];
  dealer: Dealer;
  postList: string[] = [];
  woSiteList: string[] = [];
  casingStatusList: string[] = [];
  processCodesList: string[] = [];

  // Direct customer code lookup state
  customerCodeLookupLoading = false;
  _lastSelectedCustomerCode = '';
  showSequenceNumFilter = false;
  isRestoreState: boolean;
  isCasingSearchRoute: boolean = false;
  private destroy$ = new Subject<void>();

  // Flags the Max field when the group-level circumferenceRange error is set and the field was interacted with
  circumferenceRangeMatcher: ErrorStateMatcher = {
    isErrorState: (control: AbstractControl | null): boolean =>
      !!control && (control.invalid || !!control.parent?.hasError('circumferenceRange')) && (control.touched || control.dirty)
  };

  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('customerModal') customerModal: ModalComponent;
  @ViewChild('customerModalContent') customerModalContent: TemplateRef<any>;

  public translationService = inject(TranslationService);
  public fb = inject(FormBuilder);
  public casingSearchService = inject(CasingSearchService);
  public store = inject(Store);
  public router = inject(Router);
  public plantService = inject(PlantService);
  public SiteService = inject(SiteService);
  public alertService = inject(AlertService);
  public processCodeService = inject(ProcessCodeService);
  private createWorkOrderService = inject(CreateWorkOrderService);
  private casingDropdownDataService = inject(CasingDropdownDataService);
  private restDealerService = inject(RestDealerService);
  public routeTracker = inject(RouteTrackerService);
  private featureflagsService = inject(FeatureflagsService);

  /** Make, Series, Size, Tread, Tread Width and Grade are multi-select only when the dealer flag is on; otherwise single-select. */
  isMultiSelectTreadWidthGradeEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.MULTI_SELECT_TREAD_WIDTH_GRADE_ENABLED);

  constructor() {
    this.searchCasingsForm = this.fb.group(
      {
        barcodeNumber: [''],
        casingMake: [this.isMultiSelectTreadWidthGradeEnabled ? [] : null],
        casingSeries: [this.isMultiSelectTreadWidthGradeEnabled ? [] : null],
        casingSize: [this.isMultiSelectTreadWidthGradeEnabled ? [] : null],
        casingTread: [this.isMultiSelectTreadWidthGradeEnabled ? [] : this.casingTreadList],
        casingTreadWidth: [this.isMultiSelectTreadWidthGradeEnabled ? [] : this.casingTreadWidthList],
        casingGrade: [this.isMultiSelectTreadWidthGradeEnabled ? [] : this.casingGradeList],
        casingMfgDot: [''],
        casingBrand: ['', [Validators.maxLength(25), optionalBrandNumber()]],
        casingNtr: [''],
        casingMinCircumference: ['', [optionalNumber(0)]],
        casingMaxCircumference: ['', [optionalNumber(0)]],
        casingReceivedDateStart: null,
        casingReceivedDateEnd: null,
        woCreatedDateStart: null,
        woCreatedDateEnd: null,
        woDueDateStart: null,
        woDueDateEnd: null,
        casingCompleteDateStart: null,
        casingCompleteDateEnd: null,
        status: [this.isMultiSelectTreadWidthGradeEnabled ? [] : ''],
        location: [this.isMultiSelectTreadWidthGradeEnabled ? [] : ''],
        casingLocation: [''], // Changed from [[]] to [''] for single selection
        post: [this.isMultiSelectTreadWidthGradeEnabled ? [] : ''],
        woSite: [this.isMultiSelectTreadWidthGradeEnabled ? [] : ''],
        processCodes: [this.isMultiSelectTreadWidthGradeEnabled ? [] : ''],
        customerCode: null,
        customerName: [''],
        customerPk: [''], // Hidden field to store selected customer's primary key
        sequenceNum: ['']
      },
      { validators: [this.circumferenceRangeValidator()] }
    );

    this.isRestoreState = this.store.selectSnapshot(CasingSearchState.isRestoreState) || false;
    this.isCasingSearchRoute =
      this.routeTracker.getPreviousUrl()?.includes('/home/search-casings') ||
      this.routeTracker.getPreviousUrl()?.includes('/home/work-orders') ||
      false;
  }

  ngOnInit(): void {
    this.loadDropdownLists();

    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.userLogin = this.store.selectSnapshot(DealerState.currentUser);
    // Look for sequence flag in Work Order Settings, call work order settings API if not available in store
    try {
      this.showSequenceNumFilter = this.store.selectSnapshot(DealerState.workOrderSettings).sequenceNumFlag === 'Y';
    } catch {
      this.restDealerService
        .getWorkOrderSettings(this.dealer.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.showSequenceNumFilter = res.data?.sequenceNumFlag === 'Y';
        });
    }

    const locationsLoaded = this.loadCasingLocations();
    this.loadPosts();
    this.loadWoSites();
    this.loadCasingStatus();
    this.loadProcessCodes();

    if (this.isRestoreState && this.isCasingSearchRoute) {
      Promise.all([
        locationsLoaded,
        this.casingMakeList,
        this.casingSeriesList,
        this.casingSizeList,
        this.casingTreadList,
        this.casingTreadWidthList,
        this.casingGradeList
      ]).then(() => {
        const storedCriteria = this.store.selectSnapshot(CasingSearchState.searchCriteria);
        if (storedCriteria) {
          const restoredFilters = this.mapStoredFiltersToFormFormat(storedCriteria);
          this.searchCasingsForm.patchValue(restoredFilters, { emitEvent: false });
        }
        this.store.dispatch(new UpdateIsRestoreStateAction(false));
      });
    }

    // Debounced direct lookup by customer code (mirrors createworkorder pattern)
    this.searchCasingsForm
      .get('customerCode')
      ?.valueChanges.pipe(
        tap((value: string) => {
          if (!value) this.clearLookupError('customerCode');
        }),
        debounceTime(1500),
        distinctUntilChanged(),
        filter((value: string) => !!value && value !== this._lastSelectedCustomerCode),
        takeUntil(this.destroy$)
      )
      .subscribe((value: string) => {
        this.lookupCustomerByCode(value);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadDropdownLists(): void {
    this.casingDropdownDataService
      .getCasingTreadWidthList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => (this.casingTreadWidthList = list));
    this.casingDropdownDataService
      .getCasingMakeList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => (this.casingMakeList = list));
    this.casingDropdownDataService
      .getCasingSeriesList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => (this.casingSeriesList = list));
    this.casingDropdownDataService
      .getCasingSizeList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => (this.casingSizeList = list));
    this.casingDropdownDataService
      .getCasingTreadList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => (this.casingTreadList = list));
    this.casingDropdownDataService
      .getCasingGradeList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => (this.casingGradeList = list));
  }

  async loadCasingLocations(): Promise<void> {
    await this.fetchPlants();
    await this.fetchSites();
    // Combine plant and site names into a single list for locations
    this.casingLocationList = [
      ...this.plantNames.map((plant) => `${plant.code}-${plant.name}`),
      ...this.siteNames.map((site) => `${site.code}-${site.name}`),
      'IN TRANSIT'
    ];
  }

  loadPosts(): void {
    // Example static data, replace with service/API call if needed
    this.postList = [
      this.translationService.translate('adjustmentText'),
      this.translationService.translate('bufferText'),
      this.translationService.translate('builderText'),
      this.translationService.translate('casingEditText'),
      this.translationService.translate('cementText'),
      this.translationService.translate('chamberText'),
      this.translationService.translate('ciaText'),
      this.translationService.translate('customMoldText'),
      this.translationService.translate('d2dReturnProcessingText'),
      this.translationService.translate('dealerTransferText'),
      this.translationService.translate('finalInspectionText'),
      this.translationService.translate('initialInspectionText'),
      this.translationService.translate('hyphenPreMoldText'),
      this.translationService.translate('quickReceivingText'),
      this.translationService.translate('receivingText'),
      this.translationService.translate('actionRejectText'),
      this.translationService.translate('repairText'),
      this.translationService.translate('shippingText'),
      this.translationService.translate('skiveText'),
      this.translationService.translate('sidewallRenewalText'),
      this.translationService.translate('treadPreparationText')
    ];
  }

  loadWoSites(): void {
    // Example static data, replace with service/API call if needed
    this.fetchSites();
    this.woSiteList = this.siteNames.map((site) => `${site.code}-${site.name}`);
  }

  loadCasingStatus(): void {
    // Example static data, replace with service/API call if needed
    const baseStatusList = [
      'RELEASED',
      'SHOP FLOOR',
      'CASING COMPLETION',
      'COMPLETE',
      'POSTED',
      'EDITABLE',
      'IN STOCK',
      'IN TRANSFER',
      'INCOMPLETE',
      'REJECT PENDING',
      'REJECT FOR ADMIN'
    ];
    if (this.dealer.displayDeleteCasing === 'Y') {
      this.casingStatusList = [...baseStatusList, 'DELETED'];
    } else {
      this.casingStatusList = baseStatusList;
    }
  }

  loadProcessCodes(): void {
    this.processCodesList = ['BS', 'DS', 'DT', 'IO', 'AD', 'RO', 'RR', 'CC', 'CP', 'SO', 'SC', 'RS', 'NP'];
    /*this.processCodeService.getProcessCodes(this.dealer.id).subscribe({
      next: (codes: ProcessCode[]) => {
        this.processCodesList = codes.map((codeObj) => codeObj.processCode);
      },
      error: () => {
        this.processCodesList = [];
      }
    });*/
  }

  fetchPlants(): Promise<void> {
    return new Promise((resolve) => {
      const plants = this.store.selectSnapshot(DealerState.plants);
      if (plants && plants.length > 0) {
        this.plantNames = plants; // Array of plant objects
        resolve();
      } else {
        this.plantService.getPlants(this.dealer.id).subscribe((plants) => {
          this.store.dispatch(new UpdatePlantsAction(plants));
          this.plantNames = plants; // Array of plant objects
          resolve();
        });
      }
    });
  }

  fetchSites(): Promise<void> {
    return new Promise((resolve) => {
      const sites = this.store.selectSnapshot(DealerState.sites);
      if (sites && sites.length > 0) {
        this.siteNames = sites; // Array of site objects
        resolve();
      } else {
        this.SiteService.getSites(this.dealer.id).subscribe((sites) => {
          this.store.dispatch(new UpdateSitesAction(sites));
          this.siteNames = sites; // Array of site objects
          resolve();
        });
      }
    });
  }

  clearFilters(): void {
    this._lastSelectedCustomerCode = '';
    this.searchCasingsForm.reset({
      barcodeNumber: null,
      casingReceivedDateStart: null,
      casingReceivedDateEnd: null,
      woCreatedDateStart: null,
      woCreatedDateEnd: null,
      woDueDateStart: null,
      woDueDateEnd: null,
      casingCompleteDateStart: null,
      casingCompleteDateEnd: null,
      casingTread: this.isMultiSelectTreadWidthGradeEnabled ? [] : null,
      casingTreadWidth: this.isMultiSelectTreadWidthGradeEnabled ? [] : null,
      casingGrade: this.isMultiSelectTreadWidthGradeEnabled ? [] : null,
      casingMfgDot: null,
      casingBrand: null,
      casingNtr: null,
      casingMinCircumference: '',
      casingMaxCircumference: '',
      casingMake: this.isMultiSelectTreadWidthGradeEnabled ? [] : null,
      casingSeries: this.isMultiSelectTreadWidthGradeEnabled ? [] : null,
      casingSize: this.isMultiSelectTreadWidthGradeEnabled ? [] : null,
      treadDesc: null,
      status: this.isMultiSelectTreadWidthGradeEnabled ? [] : null,
      location: this.isMultiSelectTreadWidthGradeEnabled ? [] : null,
      post: this.isMultiSelectTreadWidthGradeEnabled ? [] : '',
      woSite: this.isMultiSelectTreadWidthGradeEnabled ? [] : '',
      processCodes: this.isMultiSelectTreadWidthGradeEnabled ? [] : '',
      customerCode: null,
      customerName: null
    });
    this.searchResults$ = of(null);

    // Clear search results from state
    this.store.dispatch(new ClearCasingSearchResultsAction());
  }

  submitSearch(): void {
    this.search();
  }

  onBrandNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = sanitizeBrandNumber(input.value);
    input.value = value;
    this.searchCasingsForm.get('casingBrand')?.setValue(value, { emitEvent: false });
  }

  onCircumferenceInput(event: Event, controlName: 'casingMinCircumference' | 'casingMaxCircumference'): void {
    const input = event.target as HTMLInputElement;
    const [int, ...frac] = input.value.replace(/[^0-9.]/g, '').split('.');
    const value = frac.length ? `${int}.${frac.join('')}` : int;
    input.value = value;
    this.searchCasingsForm.get(controlName)?.setValue(value, { emitEvent: false });
  }

  private circumferenceRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const min = parseFloat(group.get('casingMinCircumference')?.value);
      const max = parseFloat(group.get('casingMaxCircumference')?.value);
      return Number.isFinite(min) && Number.isFinite(max) && min > max ? { circumferenceRange: true } : null;
    };
  }

  // Non-finite values (e.g. very long digit strings -> Infinity) would serialize as null, so drop them
  private toCircumferenceBound(value: unknown): number | undefined {
    if (value === '' || value == null) return undefined;
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  }

  search(): void {
    this.searchCasingsForm.markAllAsTouched();
    if (this.searchCasingsForm.invalid) {
      return;
    }

    this.isLoading = true;

    const formValue = this.searchCasingsForm.value;
    const searchRequest: CasingSearchRequest = {
      // Map form values to search request using correct field names
      casingMakeId: this.isAllSelected(formValue.casingMake, this.casingMakeList)
        ? null
        : this.getIdsForSelectedLabels(formValue.casingMake, this.store.selectSnapshot(CasingDropdownDataState.makes), true),
      casingSeriesId: this.isAllSelected(formValue.casingSeries, this.casingSeriesList)
        ? null
        : this.getIdsForSelectedLabels(formValue.casingSeries, this.store.selectSnapshot(CasingDropdownDataState.series)),
      casingSizeId: this.isAllSelected(formValue.casingSize, this.casingSizeList)
        ? null
        : this.getIdsForSelectedLabels(formValue.casingSize, this.store.selectSnapshot(CasingDropdownDataState.sizes)),
      ...this.buildTreadWidthGradeCriteria(formValue),
      ...this.buildLocationStatusCriteria(formValue),
      mfgDot: formValue.casingMfgDot,
      brandNum: formValue.casingBrand,
      ntr: formValue.casingNtr,
      buffCircumMin: this.toCircumferenceBound(formValue.casingMinCircumference),
      buffCircumMax: this.toCircumferenceBound(formValue.casingMaxCircumference),
      casingId: formValue.barcodeNumber,
      casingReceivedDateStart: this.dateFormatter(formValue.casingReceivedDateStart, true),
      casingReceivedDateEnd: this.dateFormatter(formValue.casingReceivedDateEnd, true, true),
      woDueDateStart: this.dateFormatter(formValue.woDueDateStart, false),
      woDueDateEnd: this.dateFormatter(formValue.woDueDateEnd, false),
      casingCompleteDateStart: this.dateFormatter(formValue.casingCompleteDateStart, true),
      casingCompleteDateEnd: this.dateFormatter(formValue.casingCompleteDateEnd, true, true),
      woCreatedDateStart: this.dateFormatter(formValue.woCreatedDateStart, false),
      woCreatedDateEnd: this.dateFormatter(formValue.woCreatedDateEnd, false),
      customerFks: formValue.customerPk ? [formValue.customerPk] : undefined,
      customerCode: formValue.customerCode,
      customerName: formValue.customerName,
      sequenceNum: this.showSequenceNumFilter && formValue.sequenceNum ? +formValue.sequenceNum : null, // Sequence number filter, only included if the flag is true
      page: 0, // Always start from first page for new search
      size: 10 // Default page size for initial search
    };

    this.casingSearchService
      .searchCasings(searchRequest)
      .pipe(
        catchError((error) => {
          this.displayError(error);
          this.store.dispatch(new ClearCasingSearchResultsAction());
          this.isLoading = false;
          return of(null);
        })
      )
      .subscribe((response) => {
        this.isLoading = false;

        if (response) {
          if (response.casingSearchResults && response.casingSearchResults.length > 0) {
            // Store search criteria and results in state
            this.store.dispatch(new UpdateSearchCriteriaAction(searchRequest));
            this.store.dispatch(new UpdateCasingSearchResultsAction(response));

            // Mark that we just performed a live search so the results page can reuse
            // these results for page 1 without an extra immediate fetch. This flag is
            // in-memory only and resets on page reload, so a hard refresh always re-fetches.
            this.casingSearchService.justSearched = true;

            // Navigate to results page
            this.router.navigate(['/home/search-casings/results']);
          } else {
            // No results found
            this.store.dispatch(new ClearCasingSearchResultsAction());
            this.alertService.showAlert({
              type: AlertType.strong,
              content: this.translationService.translate('casingsNotFoundText'),
              timestamp: 1000
            } as Alert);
          }
        }
      });
  }

  onPageChange(page: number): void {
    this.searchCasingsForm.patchValue({ page });
    this.search();
  }

  onPageSizeChange(pageSize: number): void {
    this.searchCasingsForm.patchValue({ pageSize, page: 1 });
    this.search();
    let formData = this.searchCasingsForm.controls;
    for (const fieldValue in formData) {
      if (formData[fieldValue].value !== null) {
        console.log(formData[fieldValue].value);
      }
    }
  }

  private dateFormatter(dateString: string, formatTime: boolean, endOfDay = false): string {
    if (dateString == null || dateString == '') {
      return null;
    }
    const date = new Date(dateString);
    // Make the end bound inclusive of the whole local day before converting to UTC
    if (formatTime && endOfDay) {
      date.setHours(23, 59, 59, 0);
    }
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    if (formatTime) {
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }
    return `${year}-${month}-${day}`;
  }

  // Stored datetimes are zone-less UTC strings; without 'Z' Date would read them as local time.
  private utcStringToLocalDate(value?: string): Date | null {
    return value ? new Date(value + 'Z') : null;
  }

  openCustomerModal(): void {
    this.customerModal.open({
      id: 'customer-select',
      width: 800,
      title: 'Select Customer',
      content: this.customerModalContent,
      confirm: 'Close'
    });
  }

  onCustomerSelected(customer: any): void {
    if (customer) {
      this.searchCasingsForm.patchValue({
        customerCode: customer.code,
        customerName: customer.customerName,
        customerPk: customer.customerPk
      });
      // Track the value so the debounced lookup doesn't re-fire
      this._lastSelectedCustomerCode = customer.code || '';
      this.clearLookupError('customerCode');
      if (this.customerModal && this.customerModal.dialog) {
        this.customerModal.dialog.closeAll();
      }
    }
  }

  /** Remove the lookupFailed error from a field, restoring validator-driven errors. */
  private clearLookupError(fieldName: string): void {
    const ctrl = this.searchCasingsForm.get(fieldName);
    if (!ctrl || !ctrl.hasError('lookupFailed')) return;
    const errorsCopy = { ...(ctrl.errors as any) };
    delete errorsCopy.lookupFailed;
    ctrl.setErrors(Object.keys(errorsCopy).length > 0 ? errorsCopy : null);
  }

  /** Clear all stored customer state and reset dependent form fields. */
  private clearCustomerSelection(keepCode = ''): void {
    this._lastSelectedCustomerCode = keepCode;
    this.searchCasingsForm.patchValue(
      {
        customerCode: keepCode,
        customerName: '',
        customerPk: ''
      },
      { emitEvent: false }
    );
  }

  /**
   * Look up a customer by typing directly into the customerCode field.
   * On a successful match the customer fields are populated.
   * On no match an inline lookupFailed error is shown.
   */
  private lookupCustomerByCode(searchCode: string): void {
    if (!this.dealer?.id || !searchCode?.trim()) return;

    this.customerCodeLookupLoading = true;
    this.clearLookupError('customerCode');

    this.createWorkOrderService
      .lookupCustomerByCode(this.dealer.id, this.userLogin, this.plantId || '', searchCode.trim(), false, true, 'CUSTOMER,ASSOCIATE_DEALER')
      .pipe(takeUntil(this.destroy$))
      .subscribe((customer: any) => {
        this.customerCodeLookupLoading = false;
        if (customer) {
          this.onCustomerSelected(customer);
        } else {
          const msg = `No customer found for Customer Code "${searchCode}".`;
          this.clearCustomerSelection(searchCode);
          const ctrl = this.searchCasingsForm.get('customerCode');
          ctrl?.setErrors({ ...(ctrl.errors ?? {}), lookupFailed: msg });
          ctrl?.markAsTouched();
        }
      });
  }

  private getIdForSelectedDropdownItem(selectedItem: string, dropdownData: Array<AutocompleteOption>): string | undefined {
    const selectedOption = dropdownData.find((option) => this.translationService.translate(option.code) === selectedItem);
    return selectedOption ? selectedOption.id : undefined;
  }

  /** Wraps a legacy single value in an array; empty result is undefined. */
  private toLabelArray(value: string[] | string | null | undefined): string[] | undefined {
    const list = Array.isArray(value) ? value : value ? [value] : [];
    return list.length ? list : undefined;
  }

  /** Tread, Tread Width and Grade criteria: multi-select when the flag is on, otherwise the single-select shape (at most one value). */
  private buildTreadWidthGradeCriteria(formValue: any): Pick<CasingSearchRequest, 'treadDescId' | 'treadWidthId' | 'casingGrade'> {
    const treads = this.store.selectSnapshot(CasingDropdownDataState.treads);
    const treadWidths = this.store.selectSnapshot(CasingDropdownDataState.treadWidths);
    if (!this.isMultiSelectTreadWidthGradeEnabled) {
      const treadId = formValue.casingTread ? this.getIdForSelectedDropdownItem(formValue.casingTread, treads) : undefined;
      const treadWidthId = formValue.casingTreadWidth
        ? this.getIdForSelectedDropdownItem(formValue.casingTreadWidth, treadWidths)
        : undefined;
      return {
        treadDescId: treadId ? [treadId] : undefined,
        treadWidthId: treadWidthId ? [treadWidthId] : undefined,
        casingGrade: formValue.casingGrade ? [formValue.casingGrade] : undefined
      };
    }
    return {
      treadDescId: this.isAllSelected(formValue.casingTread, this.casingTreadList)
        ? null
        : this.getIdsForSelectedLabels(formValue.casingTread, treads),
      treadWidthId: this.isAllSelected(formValue.casingTreadWidth, this.casingTreadWidthList)
        ? null
        : this.getIdsForSelectedLabels(formValue.casingTreadWidth, treadWidths),
      casingGrade: this.isAllSelected(formValue.casingGrade, this.casingGradeList) ? null : this.toLabelArray(formValue.casingGrade)
    };
  }

  /** One multi-select filter: all selected means no filter (null); flag off keeps the single-value shape. */
  private toListCriteria(
    selected: string[] | string | null,
    options: string[],
    transform: (v: string) => string = (v) => v
  ): string[] | null | undefined {
    if (this.isMultiSelectTreadWidthGradeEnabled && this.isAllSelected(selected, options)) return null;
    const list = this.toLabelArray(selected)?.map(transform);
    return this.isMultiSelectTreadWidthGradeEnabled ? list : list?.slice(0, 1);
  }

  /** Casing Location, Post, WO Site, Casing Status and Process Code criteria. */
  private buildLocationStatusCriteria(
    formValue: any
  ): Pick<CasingSearchRequest, 'shippingLocation' | 'postMarker' | 'siteFk' | 'casingStatus' | 'processCode'> {
    const underscore = (v: string) => v.replace(/ /g, '_');
    return {
      shippingLocation: this.toListCriteria(formValue.location, this.casingLocationList, (v) => v.split('-')[0]),
      postMarker: this.toListCriteria(formValue.post, this.postList, underscore),
      siteFk: this.toListCriteria(formValue.woSite, this.woSiteList),
      casingStatus: this.toListCriteria(formValue.status, this.casingStatusList, underscore),
      processCode: this.toListCriteria(formValue.processCodes, this.processCodesList)
    };
  }

  /** Selecting every option is equivalent to no filter. */
  private isAllSelected(selected: string[] | string | null, options: string[]): boolean {
    if (!Array.isArray(selected) || !options?.length) return false;
    // Options can contain duplicate labels while the selection is de-duplicated, so compare unique values
    const chosen = new Set(selected);
    return [...new Set(options)].every((option) => chosen.has(option));
  }

  /** Maps selected labels to IDs; unresolved labels are dropped, empty result is undefined. Makes use 'code - description' labels. */
  private getIdsForSelectedLabels(labels: string[] | string | null, data: Array<AutocompleteOption>, isMake = false): string[] | undefined {
    const ids = (Array.isArray(labels) ? labels : labels ? [labels] : [])
      .map((label) =>
        isMake
          ? data?.find(
              (make) => `${this.translationService.translate(make.code)} - ${this.translationService.translate(make.description)}` === label
            )?.id
          : this.getIdForSelectedDropdownItem(label, data ?? [])
      )
      .filter((id): id is string => !!id);
    return ids.length ? ids : undefined;
  }

  /** Maps stored IDs (or a legacy single ID) back to labels. */
  private getLabelsForStoredIds(
    ids: string[] | string | undefined,
    data: Array<AutocompleteOption>,
    toLabel: (o: AutocompleteOption) => string
  ): string[] {
    return (Array.isArray(ids) ? ids : ids ? [ids] : [])
      .map((id) => data?.find((option) => option.id === id))
      .filter((option): option is AutocompleteOption => !!option)
      .map(toLabel);
  }

  /**
   * Maps stored search criteria (which contain IDs/codes only) back to the form's display-value format.
   * This is needed when restoring filters from the store after navigating back to this page.
   */
  private mapStoredFiltersToFormFormat(storedCriteria: CasingSearchRequest): any {
    const makes: Array<AutocompleteOption> = this.store.selectSnapshot(CasingDropdownDataState.makes);
    const multi = this.isMultiSelectTreadWidthGradeEnabled;
    const casingMakeLabels = this.getLabelsForStoredIds(
      storedCriteria.casingMakeId,
      makes,
      (make) => `${this.translationService.translate(make.code)} - ${this.translationService.translate(make.description)}`
    );

    const series: Array<AutocompleteOption> = this.store.selectSnapshot(CasingDropdownDataState.series);
    const seriesLabels = this.getLabelsForStoredIds(storedCriteria.casingSeriesId, series, (o) =>
      this.translationService.translate(o.code)
    );

    const sizes: Array<AutocompleteOption> = this.store.selectSnapshot(CasingDropdownDataState.sizes);
    const sizeLabels = this.getLabelsForStoredIds(storedCriteria.casingSizeId, sizes, (o) => this.translationService.translate(o.code));

    const treads: Array<AutocompleteOption> = this.store.selectSnapshot(CasingDropdownDataState.treads);
    const treadLabels = this.getLabelsForStoredIds(storedCriteria.treadDescId, treads, (o) => this.translationService.translate(o.code));

    const treadWidths: Array<AutocompleteOption> = this.store.selectSnapshot(CasingDropdownDataState.treadWidths);
    const treadWidthLabels = this.getLabelsForStoredIds(storedCriteria.treadWidthId, treadWidths, (o) =>
      this.translationService.translate(o.code)
    );

    const casingMake = multi ? casingMakeLabels : casingMakeLabels[0] ?? null;
    const casingSeries = multi ? seriesLabels : seriesLabels[0] ?? null;
    const casingSize = multi ? sizeLabels : sizeLabels[0] ?? null;
    const casingTread = multi ? treadLabels : treadLabels[0] ?? null;
    const casingTreadWidth = multi ? treadWidthLabels : treadWidthLabels[0] ?? null;
    const storedGrades = this.toLabelArray(storedCriteria.casingGrade);
    const casingGrade = multi ? storedGrades ?? [] : storedGrades?.[0] ?? null;

    const asForm = (values: string[] | string | undefined, map: (v: string) => string | undefined = (v) => v) => {
      const list = (this.toLabelArray(values) ?? []).map(map).filter((v): v is string => !!v);
      return multi ? list : list[0] ?? '';
    };
    const location = asForm(storedCriteria.shippingLocation, (code) =>
      this.casingLocationList.find((entry) => entry.split('-')[0] === code)
    );
    return {
      barcodeNumber: storedCriteria.casingId || '',
      casingMake,
      casingSeries,
      casingSize,
      casingTread,
      casingTreadWidth,
      casingGrade,
      casingMfgDot: storedCriteria.mfgDot || '',
      casingBrand: storedCriteria.brandNum || '',
      casingNtr: storedCriteria.ntr || '',
      casingMinCircumference: storedCriteria.buffCircumMin != null ? String(storedCriteria.buffCircumMin) : '',
      casingMaxCircumference: storedCriteria.buffCircumMax != null ? String(storedCriteria.buffCircumMax) : '',
      casingReceivedDateStart: this.utcStringToLocalDate(storedCriteria.casingReceivedDateStart),
      casingReceivedDateEnd: this.utcStringToLocalDate(storedCriteria.casingReceivedDateEnd),
      woDueDateStart: storedCriteria.woDueDateStart || null,
      woDueDateEnd: storedCriteria.woDueDateEnd || null,
      casingCompleteDateStart: this.utcStringToLocalDate(storedCriteria.casingCompleteDateStart),
      casingCompleteDateEnd: this.utcStringToLocalDate(storedCriteria.casingCompleteDateEnd),
      woCreatedDateStart: storedCriteria.woCreatedDateStart || null,
      woCreatedDateEnd: storedCriteria.woCreatedDateEnd || null,
      status: asForm(storedCriteria.casingStatus, (v) => v.replace(/_/g, ' ')),
      location,
      post: asForm(storedCriteria.postMarker, (v) => v.replace(/_/g, ' ')),
      woSite: asForm(storedCriteria.siteFk),
      processCodes: asForm(storedCriteria.processCode),
      customerCode: storedCriteria.customerCode || '',
      customerName: storedCriteria.customerName || '',
      customerPk: storedCriteria.customerFks?.[0] || '',
      sequenceNum: storedCriteria.sequenceNum != null ? String(storedCriteria.sequenceNum) : ''
    };
  }

  private displayError(error: HttpErrorResponse) {
    const alertContent =
      error.error.statusCode === 404
        ? error.error.message + '.' + this.translationService.translate('contactSupportText')
        : this.translationService.translate('tryAgainText');
    this.alertService.showAlert({
      type: AlertType.critical,
      content: alertContent
    } as Alert);
  }

  /**
   * Filter NTR input to allow only numeric characters (0-9)
   * Restricts to single digit and removes any non-numeric input
   */
  onNtrInput(event: any): void {
    const input = event.target;
    // Remove any non-numeric characters
    let value = input.value.replace(/[^0-9]/g, '');
    // Restrict to single digit (maxlength is also set but this ensures enforcement)
    value = value.substring(0, 1);
    // Update the form control with the filtered value
    this.searchCasingsForm.get('casingNtr')?.setValue(value, { emitEvent: false });
    input.value = value;
  }
}
